import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, User, Phone, Building, Car, Users, X, Briefcase } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(true);
  const [selectedRole, setSelectedRole] = useState<'user' | 'driver' | 'promotor'>('user');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    // Validate company name for promotors
    if (selectedRole === 'promotor' && !formData.companyName.trim()) {
      toast.error('Company name is required for promotor accounts');
      return;
    }

    try {
      setLoading(true);
      await signUp(
        formData.email, 
        formData.password, 
        formData.fullName, 
        formData.phone, 
        selectedRole,
        selectedRole === 'promotor' ? formData.companyName : undefined
      );
      toast.success('Registration successful! Please check your email to confirm your account.');
      navigate('/login');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to register. Please try again.');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: 'user' | 'driver' | 'promotor') => {
    setSelectedRole(role);
    setShowRoleModal(false);
  };

  const accountTypes = [
    {
      id: 'user',
      label: 'User',
      description: 'Regular user account for browsing events and general access',
      icon: Users
    },
    {
      id: 'promotor',
      label: 'Promotor',
      description: 'For event organizers and track promoters who need to book track time',
      icon: Briefcase
    },
    {
      id: 'driver',
      label: 'Driver',
      description: 'For racers who want to participate in events and competitions',
      icon: Car
    }
  ];

  return (
    <div className="min-h-screen bg-black py-20">
      {/* Account Type Selection Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-xl w-full max-w-3xl p-8 shadow-lg border border-zinc-700">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Choose Account Type</h2>
                <p className="text-gray-400 mt-1 text-sm">
                  Select the account that best fits your needs at <span className="font-semibold text-white">Jacky Circuit</span>.
                </p>
              </div>
              <button onClick={() => setShowRoleModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
      
            <div className="grid gap-4 sm:grid-cols-3">
              {accountTypes.map(({ id, label, description, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => handleRoleSelect(id as 'user' | 'driver' | 'promotor')}
                  className={`rounded-lg border p-5 flex flex-col items-center text-center transition-all duration-200 ${
                    selectedRole === id
                      ? 'bg-primary-600 border-primary-500 text-white shadow-lg'
                      : 'bg-zinc-800 border-zinc-700 text-gray-300 hover:bg-zinc-700'
                  }`}
                >
                  <Icon className="w-10 h-10 mb-4" />
                  <span className="text-lg font-semibold">{label}</span>
                  <p className="text-sm mt-1 text-zinc-400">{description}</p>
                </button>
              ))}
            </div>
      
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 mb-4">
                This helps tailor your experience.
              </p>
              <button
                onClick={() => setShowRoleModal(false)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition"
              >
                {selectedRole === 'driver' ? <Car className="w-4 h-4" /> :
                 selectedRole === 'promotor' ? <Briefcase className="w-4 h-4" /> :
                 <Users className="w-4 h-4" />}
                <span>Continue as {accountTypes.find(t => t.id === selectedRole)?.label}</span>
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-zinc-900 rounded-lg p-8">
          <h1 className="text-3xl font-bold text-white mb-8 text-center">
            Join Jacky Circuit
          </h1>

          {/* Account Type Tabs */}
          <div className="mb-8 relative">
            <div className="flex border-b border-zinc-700">
              {accountTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedRole(type.id as 'user' | 'driver' | 'promotor')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    selectedRole === type.id
                      ? 'text-primary-500 border-b-2 border-primary-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {type.label}
                  {selectedRole === type.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 transform translate-y-0.5"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-300">
                Full Name
              </label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  id="fullName"
                  required
                  className="mt-1 block w-full rounded-md bg-zinc-800 border-zinc-700 text-white px-4 py-2 pl-10 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                />
              </div>
            </div>

            {selectedRole === 'promotor' && (
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-300">
                  Company Name *
                </label>
                <div className="relative mt-1">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="companyName"
                    required
                    className="mt-1 block w-full rounded-md bg-zinc-800 border-zinc-700 text-white px-4 py-2 pl-10 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-400">
                  You'll need to complete your profile with CRIB and KVK numbers after registration
                </p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email Address
              </label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  required
                  className="mt-1 block w-full rounded-md bg-zinc-800 border-zinc-700 text-white px-4 py-2 pl-10 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                Phone Number
              </label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  id="phone"
                  required
                  className="mt-1 block w-full rounded-md bg-zinc-800 border-zinc-700 text-white px-4 py-2 pl-10 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <PasswordInput
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              label="Password"
              required
              minLength={6}
            />

            <PasswordInput
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              label="Confirm Password"
              required
              minLength={6}
              showStrengthMeter={false}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'Registering...' : `Register as ${accountTypes.find(type => type.id === selectedRole)?.label}`}
            </button>

            <div className="text-center mt-4">
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-500 hover:text-primary-400">
                  Login here
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}